import { ClassNameOnlyProps } from 'module/types/common';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@rocketlane/ui';
import {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

const RTETruncateStyle = css`
  .rte-editor {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: calc(1em * 2);
    white-space: normal !important;
  }
  .rte-editor ul,
  .rte-editor [data-type='taskList'] {
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .rte-editor li,
  .rte-editor [data-type='taskList'] li {
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .rte-editor [data-type='taskList'] li > div {
    display: inline !important;
  }

  .rte-editor [data-type='taskList'] li label {
    display: inline !important;
  }

  .rte-editor [data-type='taskList'] li div p {
    display: inline !important;
    margin: 0 !important;
    padding: 0;
  }
`;
const DropdownButton = styled(Button)``;
const ContentWrapper = styled.div<{
  isCollapsed: boolean;
  collapsible: boolean;
  contentBackgroundColor?: string;
}>`
  border-radius: var(--border-radius-default);
  position: relative;
  padding: var(--spacing-03, 0.5rem) var(--spacing-04, 0.75rem);
  ${({ isCollapsed }) =>
    isCollapsed &&
    css`
      max-height: 40px;
      overflow: hidden;
    `};
  ${({ collapsible }) =>
    collapsible &&
    css`
      cursor: pointer;
      padding-right: calc(var(--spacing-04, 0.75rem) + 24px);
    `};
  ${({ isCollapsed }) =>
    isCollapsed &&
    css`
      ${RTETruncateStyle}
    `};
  ${({ contentBackgroundColor }) =>
    contentBackgroundColor &&
    css`
      background-color: ${contentBackgroundColor};
    `};

  ${DropdownButton} {
    position: absolute;
    right: var(--spacing-04, 0.75rem);
    top: 8px;
  }
`;
const Footer = styled.div``;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;

interface SectionProps extends ClassNameOnlyProps {
  title: ReactNode;
  content: ReactNode;
  collapsible?: boolean;
  footer?: ReactNode;
  contentBackgroundColor?: string;
}

const SectionComponentBase: ForwardRefRenderFunction<
  HTMLDivElement,
  SectionProps
> = (
  {
    className,
    title,
    content,
    collapsible = true,
    footer,
    contentBackgroundColor,
  },
  ref,
) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsible ?? true);
  const toggleCollapsed = useCallback(() => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  }, [collapsible, isCollapsed]);

  return (
    <Wrapper className={className} ref={ref}>
      {title}
      <ContentWrapper
        isCollapsed={isCollapsed}
        collapsible={collapsible}
        contentBackgroundColor={contentBackgroundColor}
        onClick={toggleCollapsed}
      >
        {content}
        {collapsible && (
          <DropdownButton
            size="small"
            variant="ghost"
            onClick={toggleCollapsed}
            leftIcon={
              <FontAwesomeIcon
                icon={isCollapsed ? faChevronUp : faChevronDown}
                size={12}
              />
            }
          />
        )}
      </ContentWrapper>
      {footer && <Footer>{footer}</Footer>}
    </Wrapper>
  );
};

export const Section = forwardRef(SectionComponentBase);
